import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { qa, validation } from '../api/api';
import ConfirmationModal from '../components/ConfirmationModal';

const Projects = ({ setAlert }) => {
  const history = useHistory();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [campName, setCampName] = useState('');
  const [scorecards, setScorecards] = useState([]);
  const [selectedScorecard, setSelectedScorecard] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState({ open: false, title: '', message: '', success: false });

  useEffect(() => {
    const loadScorecards = async () => {
      try {
        const res = await qa();
        const items = res.Items || res.items || [];
        const names = [...new Set(items.map(i => i.scorecard?.S || i.template?.S).filter(Boolean))];
        setScorecards(names.map(n => ({ label: n, value: n })));
      } catch (err) {
        console.error('Failed to load scorecards:', err);
      }
    };
    loadScorecards();
  }, []);

  const steps = [
    {
      title: 'Craft your Campaign',
      content: (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
            <input
              type="text"
              value={campName}
              onChange={(e) => setCampName(e.target.value)}
              className="input-field"
              placeholder="Enter campaign name..."
            />
            <p className="text-sm text-gray-500 mt-1">Choose a descriptive name for your quality assurance campaign</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Description</label>
            <textarea rows={4} className="input-field" placeholder="Describe the purpose and scope of this campaign..." />
          </div>
        </div>
      )
    },
    {
      title: 'Select Scorecard',
      content: (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Scorecard</label>
            <select
              value={selectedScorecard}
              onChange={(e) => setSelectedScorecard(e.target.value)}
              className="select-field"
            >
              <option value="">Select scorecard...</option>
              {scorecards.map(sc => (
                <option key={sc.value} value={sc.value}>{sc.label}</option>
              ))}
            </select>
          </div>
        </div>
      )
    },
    {
      title: 'Review and Launch',
      content: (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Campaign Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-medium text-gray-700 mb-2">Campaign Details</h5>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="font-medium text-gray-500">Name:</dt>
                    <dd className="text-gray-900">{campName || 'Not specified'}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h5 className="font-medium text-gray-700 mb-2">Scorecard</h5>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="font-medium text-gray-500">Selected:</dt>
                    <dd className="text-gray-900">{selectedScorecard || 'None'}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const canProceed = () => {
    switch (activeStepIndex) {
      case 0:
        return campName.trim().length > 0;
      case 1:
        return selectedScorecard.trim().length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (activeStepIndex < steps.length - 1) {
      setActiveStepIndex(activeStepIndex + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (activeStepIndex > 0) {
      setActiveStepIndex(activeStepIndex - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await validation([{ campName, tempName: selectedScorecard }]);
      setAlert({ variant: 'success', heading: 'Campaign Created', text: 'Your campaign has been created successfully!' });
      setConfirmation({ open: true, title: 'Campaign Created', message: 'Your campaign has been created successfully!', success: true });
    } catch (error) {
      console.error('Error creating campaign:', error);
      const msg = error.message === 'Network error'
        ? 'Network error while contacting the API. Please check your connection or try again later.'
        : 'Failed to create campaign. Please try again.';
      setAlert({ variant: 'error', heading: 'Error', text: msg });
      setConfirmation({ open: true, title: 'Error', message: msg, success: false });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Create Campaign</h1>
        <p className="text-gray-600 mt-1">Set up a new quality assurance campaign</p>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <nav aria-label="Progress" className="relative w-full">
          <ol className="relative flex justify-between items-center">
            {steps.map((step, index) => (
              <li key={index} className="relative flex-1 flex flex-col items-center text-center">
                <div className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full ${index <= activeStepIndex ? 'bg-cyan-600 text-white' : 'bg-gray-300 text-gray-500'}`}> 
                  {index < activeStepIndex ? (
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                {index !== steps.length - 1 && (
                  <div className={`absolute left-1/2 top-1/2 w-full h-0.5 -z-10 transform -translate-y-1/2 ${index < activeStepIndex ? 'bg-cyan-600' : 'bg-gray-300'}`} />
                )}
                <div className={`mt-2 text-sm font-medium ${index <= activeStepIndex ? 'text-gray-900' : 'text-gray-500'}`}>{step.title}</div>
              </li>
            ))}
          </ol>
        </nav>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">{steps[activeStepIndex].title}</h2>
        {steps[activeStepIndex].content}
        <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
          <button onClick={handlePrevious} disabled={activeStepIndex === 0} className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <div className="text-sm text-gray-500">Step {activeStepIndex + 1} of {steps.length}</div>
          <button onClick={handleNext} disabled={!canProceed() || isSubmitting} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : activeStepIndex === steps.length - 1 ? (
              <>
                Create Campaign
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </>
            ) : (
              <>
                Next
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Campaign Setup Tips</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Choose descriptive names that clearly identify the campaign purpose</li>
                <li>Select an existing scorecard that matches your campaign goals</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        open={confirmation.open}
        title={confirmation.title}
        message={confirmation.message}
        onClose={() => {
          setConfirmation({ ...confirmation, open: false });
          if (confirmation.success) {
            history.push('/campaign');
          }
        }}
      />
    </div>
  );
};

export default Projects;
